import { ExposureDirectionType } from 'data/types';
import { exposureForOutwardNormal } from 'utils/edge-exposure';
import { roundToDecimal } from 'utils/helpers';
import { EdgeCardinal, SpacePolygonEdge } from 'utils/space-polygon';

export type DesiredPolygonWall = {
  cardinal: EdgeCardinal;
  width: number;
  exposure: ExposureDirectionType;
};

type WallLike = {
  uniqueId?: string;
  width: number;
  edge?: EdgeCardinal;
  exposure?: string;
};

export type WallMatch<T> = {
  desired: DesiredPolygonWall;
  existing: T | null;
};

const WALL_WIDTH_DECIMAL_PLACES = 1;

export function wallWidthForLengthFt(lengthFt: number): number {
  return Math.max(1, roundToDecimal(lengthFt, WALL_WIDTH_DECIMAL_PLACES));
}

export function polygonEdgeLengthFt(
  edge: Pick<SpacePolygonEdge, 'p1' | 'p2'>
): number {
  return wallWidthForLengthFt(
    Math.hypot(edge.p2.x - edge.p1.x, edge.p2.y - edge.p1.y)
  );
}

export function desiredWallForPolygonEdge(
  edge: SpacePolygonEdge,
  cardinal: EdgeCardinal,
  homeOrientation: ExposureDirectionType | undefined
): DesiredPolygonWall {
  return {
    cardinal,
    width: polygonEdgeLengthFt(edge),
    exposure: exposureForOutwardNormal(edge.outward, homeOrientation)
  };
}

export function desiredWallsFromPolygonEdges(
  edges: SpacePolygonEdge[],
  homeOrientation: ExposureDirectionType | undefined
): DesiredPolygonWall[] {
  const desired: DesiredPolygonWall[] = [];
  for (const edge of edges) {
    if (edge.isExterior && edge.cardinal) {
      desired.push(
        desiredWallForPolygonEdge(edge, edge.cardinal, homeOrientation)
      );
    }
  }
  return desired;
}

type WallPairing = {
  desiredIndex: number;
  wallIndex: number;
  widthRank: number;
  exposureRank: number;
  diff: number;
};

const WIDTH_TIE_TOLERANCE_FT = 0.5;

function widthRankForDiff(diff: number): number {
  return diff <= WIDTH_TIE_TOLERANCE_FT ? 0 : 1;
}

function exposureRankForPairing(
  candidate: WallLike,
  wall: DesiredPolygonWall
): number {
  return candidate.exposure === wall.exposure ? 0 : 1;
}

function candidateWallPairings<T extends WallLike>(
  existing: T[],
  desired: DesiredPolygonWall[],
  getCardinal: (wall: T) => EdgeCardinal | undefined
): WallPairing[] {
  const pairings: WallPairing[] = [];
  desired.forEach((wall, desiredIndex) => {
    existing.forEach((candidate, wallIndex) => {
      if (getCardinal(candidate) === wall.cardinal) {
        const diff = Math.abs(candidate.width - wall.width);
        pairings.push({
          desiredIndex,
          wallIndex,
          widthRank: widthRankForDiff(diff),
          exposureRank: exposureRankForPairing(candidate, wall),
          diff
        });
      }
    });
  });
  return pairings.sort(
    (a, b) =>
      a.widthRank - b.widthRank ||
      a.exposureRank - b.exposureRank ||
      a.diff - b.diff ||
      a.desiredIndex - b.desiredIndex ||
      a.wallIndex - b.wallIndex
  );
}

export function matchExistingWallsToDesired<T extends WallLike>(
  existing: T[],
  desired: DesiredPolygonWall[],
  getCardinal: (wall: T) => EdgeCardinal | undefined
): { matches: WallMatch<T>[]; unmatched: T[] } {
  const assigned: Array<T | null> = desired.map(() => null);
  const usedWalls = new Set<number>();

  for (const pairing of candidateWallPairings(existing, desired, getCardinal)) {
    const desiredFree = assigned[pairing.desiredIndex] === null;
    if (desiredFree && !usedWalls.has(pairing.wallIndex)) {
      assigned[pairing.desiredIndex] = existing[pairing.wallIndex];
      usedWalls.add(pairing.wallIndex);
    }
  }

  return {
    matches: desired.map((wall, index) => ({
      desired: wall,
      existing: assigned[index]
    })),
    unmatched: existing.filter((_, index) => !usedWalls.has(index))
  };
}

export function assignWallsToPolygonEdges<T extends WallLike>(
  edges: SpacePolygonEdge[],
  walls: T[],
  getCardinal: (wall: T) => EdgeCardinal | undefined,
  homeOrientation: ExposureDirectionType | undefined
): Array<T | null> {
  const desired = desiredWallsFromPolygonEdges(edges, homeOrientation);
  const { matches } = matchExistingWallsToDesired(walls, desired, getCardinal);

  const assigned: Array<T | null> = edges.map(() => null);
  let matchIndex = 0;
  for (let i = 0; i < edges.length; i += 1) {
    const edge = edges[i];
    if (edge.isExterior && edge.cardinal) {
      assigned[i] = matches[matchIndex]?.existing ?? null;
      matchIndex += 1;
    }
  }
  return assigned;
}
